import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  FONT_FAMILY_OPTIONS,
  type FontFamilyOptionKey,
} from "~/features/settings/constants";

interface SettingsTypographyProps {
  fontFamily: FontFamilyOptionKey;
  lineSpacing: string;
  onFontFamilyChange: (value: FontFamilyOptionKey) => void;
  onLineSpacingChange: (value: string) => void;
}

const lineSpacings = [
  {
    value: "1",
    labelId: "settings.lineSpacingSingle",
    defaultMessage: "Dense",
    lineHeight: "1",
  },
  {
    value: "1.5",
    labelId: "settings.lineSpacingStandard",
    defaultMessage: "Regular",
    lineHeight: "1.5",
  },
  {
    value: "2",
    labelId: "settings.lineSpacingDouble",
    defaultMessage: "Relaxed",
    lineHeight: "2",
  },
];

export const SettingsTypography: FC<SettingsTypographyProps> = ({
  fontFamily,
  lineSpacing,
  onFontFamilyChange,
  onLineSpacingChange,
}) => {
  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">
          <FormattedMessage
            id="settings.fontFamily"
            defaultMessage={"Font Family"}
          />
        </span>
        <Select
          value={fontFamily}
          onValueChange={(value) =>
            onFontFamilyChange(value as FontFamilyOptionKey)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a font" />
          </SelectTrigger>
          <SelectContent>
            {FONT_FAMILY_OPTIONS.map(({ value, labelId, defaultMessage }) => (
              <SelectItem key={value} value={value}>
                <FormattedMessage
                  id={labelId}
                  defaultMessage={defaultMessage}
                />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">
          <FormattedMessage
            id="settings.lineSpacing"
            defaultMessage={"Line Spacing"}
          />
        </span>
        <RadioGroup value={lineSpacing}>
          {lineSpacings.map(({ value, labelId, defaultMessage }) => (
            <RadioGroupItem
              key={value}
              value={value}
              id={`line-spacing-${value}`}
              name="lineSpacing"
              checked={lineSpacing === value}
              onChange={(e) => onLineSpacingChange(e.currentTarget.value)}
              label={
                <FormattedMessage
                  id={labelId}
                  defaultMessage={defaultMessage}
                />
              }
            />
          ))}
        </RadioGroup>
      </div>
    </div>
  );
};
